import { ReactNode, useEffect, useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { Close, ErrorOutline } from "@mui/icons-material";
import SyncIcon from "@mui/icons-material/Sync";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Badge,
  Box,
  Button,
  buttonClasses,
  Dialog,
  DialogTitle,
  IconButton,
  Portal,
  Snackbar,
  snackbarContentClasses,
  Stack,
  Tooltip,
  Typography as T,
} from "@mui/material";
import { circularProgressClasses } from "@mui/material/CircularProgress";
import useFileJobs from "files/hooks/useFileJobs";
import useFiles, { resolveRouterPath, useFilesSearch } from "files/hooks/useFiles";
import JobAction from "files/types/enums/jobAction";
import FileJob, { JobStatus } from "files/types/interfaces/fileJob";
import { StringParam, useQueryParam } from "use-query-params";
import ListCard, { ExpandableListItem } from "~/components/ListCard";
import Loader from "~/components/Loader";
import TextOverflow from "~/components/TextOverflow";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";

const { VITE_RWB_API } = import.meta.env;

const DetailRow = (props: { label: string; value: ReactNode }) => (
  <Box display="flex" gap={1}>
    <T component="div" variant="label" sx={{ whiteSpace: "nowrap" }}>
      {props.label}:
    </T>
    <T component="div" variant="body2">
      {props.value}
    </T>
  </Box>
);

const JobDetails = ({ job }: { job: FileJob }) => {
  const { data, mutate: mutateJobs } = useFileJobs();
  const { mutate: mutateFiles } = useFiles();
  const { mutate: mutateFilesSearch } = useFilesSearch();
  const [search] = useQueryParam("q", StringParam);
  const { datasetId } = useParams();
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const isActive = [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status);
  const action = isActive ? JobAction.CANCEL : JobAction.ARCHIVE;
  const ctaLabel = isActive ? "Cancel" : "Clear";
  const jobErrors = job.progress?.filter((file) => file.error_message) ?? [];

  const updateJob = async () => {
    setError(false);
    setSubmitting(true);

    const resp = await fetch(`${VITE_RWB_API}/file-browser/jobs/${job.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
      }),
    });

    if (resp.ok) {
      await mutateJobs(data?.filter(({ id }) => id !== job.id));

      if (search) {
        await mutateFilesSearch();
      }
      await mutateFiles();
    } else {
      setError(true);
    }
    setSubmitting(false);
  };

  return (
    <Stack gap={1} mx={4}>
      {error && (
        <Alert severity="error" variant="outlined" sx={{ height: "auto !important" }}>
          Unable to {ctaLabel.toLowerCase()}
        </Alert>
      )}
      <DetailRow
        label="Source"
        value={resolveRouterPath(job.source, isGlobalFileContext, datasetId)}
      />
      <DetailRow
        label="Destination"
        value={resolveRouterPath(job.destination, isGlobalFileContext, datasetId)}
      />
      {!!jobErrors.length && (
        <DetailRow
          label="Errors"
          value={jobErrors.map(({ source, error_message }) => (
            <Box key={source} sx={{ wordBreak: "break-all", mb: 0.25 }}>
              <T component="span" fontFamily="monospace" fontSize="inherit">
                {error_message}
              </T>{" "}
              - {resolveRouterPath(source, isGlobalFileContext, datasetId)}
            </Box>
          ))}
        />
      )}
      <Box display="flex" justifyContent="center">
        <LoadingButton
          fullWidth
          loading={submitting}
          onClick={updateJob}
          color="error"
          sx={{
            width: 150,
            color: "attention.main",
            [`& .${buttonClasses.loadingIndicator}`]: {
              color: "grey.500",
            },
          }}
        >
          {ctaLabel}
        </LoadingButton>
      </Box>
    </Stack>
  );
};

const FileJobs = () => {
  const title = "Recent File Actions";
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const [open, setOpen] = useState(false);
  const { data = [], notification } = useFileJobs();
  const pendingJobs = data.filter((job) =>
    [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status)
  );
  const jobsWithErrors =
    data.filter((job) => {
      return job.progress?.some((progress) => !!progress.error_message);
    }) ?? [];
  // Only show pending or completed (with error) jobs
  const jobsShown = [...pendingJobs, ...jobsWithErrors];

  useEffect(() => {
    if (!jobsShown.length) {
      setOpen(false);
    }
  }, [jobsShown]);

  return (
    <>
      {!!jobsShown.length && (
        <Tooltip title={title} arrow enterDelay={750}>
          <IconButton onClick={() => setOpen(true)} size="small">
            <Box display="flex" justifyContent="center" alignItems="center" height={24} width={24}>
              <Badge color="error" variant="dot" invisible={!jobsWithErrors?.length}>
                <SyncIcon
                  sx={{
                    fontSize: 22,
                    transform: "scaleX(-1)",
                    animation: pendingJobs.length ? "spin 2s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg) scaleX(-1)" },
                      "100%": { transform: "rotate(360deg) scaleX(-1)" },
                    },
                  }}
                />
              </Badge>
            </Box>
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { "& .MuiPaper-root": { overflowY: "auto", border: "none", height: 500 } },
        }}
      >
        <DialogTitle>
          <TextOverflow>{title}</TextOverflow>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <ListCard>
          {jobsShown.map((job) => {
            const sourcePath = resolveRouterPath(job.source ?? "", isGlobalFileContext);
            const filename = sourcePath.split("/").at(-1) ?? "";

            return (
              <ExpandableListItem
                indicator
                key={job.id}
                expansionContent={<JobDetails job={job} />}
                icon={
                  [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status) ? (
                    <Box
                      sx={{
                        [`& .${circularProgressClasses.indeterminate}`]: { color: "grey.600" },
                      }}
                    >
                      <Loader size={14} />
                    </Box>
                  ) : (
                    <ErrorOutline color="error" sx={{ fontSize: 18 }} />
                  )
                }
              >
                <Box
                  sx={{
                    wordBreak: "break-all",
                    "&::first-letter": { textTransform: "capitalize" },
                  }}
                >
                  {job.job_type} {filename}
                </Box>
              </ExpandableListItem>
            );
          })}
        </ListCard>
      </Dialog>
      <Portal>
        <Snackbar
          open={notification.visible}
          autoHideDuration={3500}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={(_, reason) => {
            if (reason !== "clickaway") {
              notification.hide();
            }
          }}
          ContentProps={{ sx: { [`& .${snackbarContentClasses.message}`]: { width: "100%" } } }}
          message={
            <Box display="flex" gap={1} alignItems="center">
              <T variant="body2" color="inherit" flex={1}>
                {notification.message}
              </T>
              {notification.undoCallback && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    notification.hide();
                    if (notification.undoCallback) {
                      notification.undoCallback();
                    }
                  }}
                >
                  Undo
                </Button>
              )}
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={notification.hide}
              >
                <Close fontSize="small" sx={{ color: "common.white" }} />
              </IconButton>
            </Box>
          }
        />
      </Portal>
    </>
  );
};

export default FileJobs;
